import React, { lazy, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import NewsletterForm from './NewsletterForm.jsx';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import logo from './assets/logo.png';
import './index.css';
import xLogo from './assets/x.png'; // Import the new X logo
import ytLogo from './assets/Youtube_logo.png';
import instagramLogo from './assets/instagram.png';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
// WhatsApp button commented out as requested
// import downloadWhatsapp from './assets/download-removebg-preview.png';

const App = lazy(() => import('./App.jsx'));
const Features = lazy(() => import('./Features.jsx'));
const Pricing = lazy(() => import('./Pricing.jsx'));
const Contact = lazy(() => import('./Contact.jsx'));
const BookDemo = lazy(() => import('./BookDemo.jsx'));

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

function Navbar() {
  const location = useLocation();
  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/features', label: 'Features' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/contact', label: 'Contact Us' },
    { to: '/book-demo', label: 'Book a Demo' },
  ];
  const [menuOpen, setMenuOpen] = React.useState(false);
  return (
    <nav className="w-full bg-white shadow sticky top-0 z-30">
      <div className="max-w-7xl mx-auto flex items-center h-20 px-6 justify-between">
        <div className="flex items-center gap-3 group">
          <Link to="/" className="flex items-center gap-3 group cursor-pointer">
            <img src={logo} alt="ZettAI Logo" className="h-10 w-auto" loading="lazy" />
            <div className="flex flex-col">
              <span className="text-2xl font-extrabold text-blue-800 tracking-tight leading-tight">ZettAI</span>
              <span className="text-xs text-gray-500 font-semibold tracking-wide mt-0.5">AI Powered Review Intelligence.</span>
            </div>
          </Link>
        </div>
        {/* Desktop nav links */}
        <div className="hidden md:flex gap-6 items-center flex-1 justify-end">
          {navLinks.map(link => (
            link.label === 'Book a Demo' ? (
              <Link
                key={link.to}
                to={link.to}
                className="bg-gradient-to-r from-blue-600 to-green-400 text-white font-semibold py-2 px-5 rounded-xl shadow-lg text-base hover:from-blue-700 hover:to-green-500 transition transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl ml-4 mr-2"
                aria-label="Book a Demo"
              >
                {link.label}
              </Link>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                className={
                  (location.pathname === link.to || (link.to !== '/' && location.pathname.startsWith(link.to)))
                    ? 'text-blue-700 font-bold transition'
                    : 'text-black font-medium hover:text-blue-700 transition'
                }
              >
                {link.label}
              </Link>
            )
          ))}

        </div>
        {/* Hamburger for mobile only */}
        <button
          className="md:hidden flex flex-col items-center justify-center w-10 h-10 focus:outline-none"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Open navigation menu"
        >
          <span className="block w-7 h-1 bg-blue-800 rounded mb-1"></span>
          <span className="block w-7 h-1 bg-blue-800 rounded mb-1"></span>
          <span className="block w-7 h-1 bg-blue-800 rounded"></span>
        </button>
      </div>
      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden w-full bg-white shadow-lg absolute left-0 top-20 z-40 animate-fade-in">
          <div className="flex flex-col items-center py-4 gap-4">
            {navLinks.map(link => (
              link.label === 'Book a Demo' ? (
                <Link
                  key={link.to}
                  to={link.to}
                  className="w-11/12 bg-gradient-to-r from-blue-600 to-green-400 text-white font-semibold py-2 px-5 rounded-xl shadow-lg text-lg hover:from-blue-700 hover:to-green-500 transition transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl text-center"
                  aria-label="Book a Demo"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={
                    (location.pathname === link.to || (link.to !== '/' && location.pathname.startsWith(link.to)))
                      ? 'text-blue-700 font-bold text-lg'
                      : 'text-gray-700 font-medium text-lg hover:text-blue-700'
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

// WhatsApp button component commented out as requested
// function WhatsAppButton() {
//   const location = useLocation();
//   const [show, setShow] = React.useState(true);

//   React.useEffect(() => {
//     if (location.pathname !== '/') {
//       setShow(true);
//       return;
//     }
//     // Only on home: hide if hero section is in view
//     function onScroll() {
//       const hero = document.querySelector('section'); // first section is hero
//       if (!hero) return setShow(true);
//       const rect = hero.getBoundingClientRect();
//       // If bottom of hero is above 80px from top, show button
//       if (rect.bottom < 80) setShow(true);
//       else setShow(false);
//     }
//     window.addEventListener('scroll', onScroll);
//     onScroll(); // run on mount
//     return () => window.removeEventListener('scroll', onScroll);
//   }, [location.pathname]);

//   if (!show) return null;
//   return (
//     <a
//       href="https://wa.me/1234567890"
//       target="_blank"
//       rel="noopener noreferrer"
//       className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg p-4 flex items-center justify-center z-50 transition mb-20"
//       title="Chat with us on WhatsApp"
//     >
//       <img src={downloadWhatsapp} alt="WhatsApp" className="w-7 h-7" />
//     </a>
//   );
// }

// ChatbotToggleController removed as n8n chat has been disabled

function Footer() {
  return (
    <footer className="w-full bg-gradient-to-b from-[#16213e] to-[#1a237e] text-white pt-12 pb-4">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Logo & Description */}
        <div>
          <div className="flex items-center mb-2">
            <img src={logo} alt="ZettAI Logo" className="h-10 w-auto mr-2" loading="lazy" />
            <div>
              <span className="text-2xl font-bold text-white">ZettAI</span><br />
              <span className="text-sm text-blue-200">Know What Matters</span>
            </div>
          </div>
          <p className="text-blue-100 text-base mt-4 mb-4 max-w-xs">Your AI-Powered Review Command Center. Transform customer feedback into actionable insights with real-time alerts and intelligent automation.</p>
          <div className="flex gap-4 mt-2 text-2xl">
            <Link to="/" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} title="X">
              <img src={xLogo} alt="X Logo" style={{height: 22, width: 22}} />
            </Link>
            <Link to="/" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} title="YouTube">
              <img src={ytLogo} alt="YouTube Logo" style={{height: 22, width: 22}} />
            </Link>
            <Link to="/" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} title="Instagram">
              <img src={instagramLogo} alt="Instagram Logo" style={{height: 22, width: 22}} />
            </Link>
          </div>
        </div>
        {/* Product */}
        <div>
          <h4 className="text-lg font-semibold text-green-200 mb-3">Product</h4>
          <ul className="space-y-2">
            <li><Link to="/features" className="hover:underline text-white">Features</Link></li>
            <li><Link to="/pricing" className="hover:underline text-white">Pricing</Link></li>
          </ul>
        </div>
        {/* Company */}
        <div>
          <h4 className="text-lg font-semibold text-green-200 mb-3">Company</h4>
          <ul className="space-y-2">
            <li><a href="https://www.metanovaai.com/about" target="_blank" rel="noopener noreferrer" className="hover:underline text-white">About Us</a></li>
            <li><a href="https://www.metanovaai.com/careers" target="_blank" rel="noopener noreferrer" className="hover:underline text-white">Careers</a></li>
            <li><Link to="/contact" className="hover:underline text-white">Contact Us</Link></li>
          </ul>
        </div>
        {/* Newsletter Form on the right side */}
        <div>
          <NewsletterForm />
        </div>
      </div>
      {/* Newsletter Subscription Form removed */}
      <div className="border-t border-blue-900 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between max-w-7xl mx-auto px-6 text-blue-200 text-sm gap-4">
        <span>© 2024 ZettAI powered by MetaNova AI. All rights reserved.</span>
        <div className="flex gap-6">
          <a href="/tos" className="hover:underline">Terms of Service</a>
          <a href="/privacy" className="hover:underline">Privacy Policy</a>
          <a href="/security" className="hover:underline">Security</a>
        </div>
      </div>
    </footer>
  );
}

// Dispatch render event for prerender plugin
if (import.meta.env.PROD) {
  document.addEventListener('DOMContentLoaded', () => {
    // Give the app a moment to render before notifying the prerenderer
    setTimeout(() => {
      document.dispatchEvent(new Event('render-event'));
    }, 500);
  });
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <ScrollToTop />
      <Navbar />
      <React.Suspense fallback={<div className="w-full text-center py-20 text-xl text-blue-700">Loading...</div>}>
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/book-demo" element={<BookDemo />} />
      </Routes>
      </React.Suspense>
      <Footer />
      <Analytics />
      <SpeedInsights />
    </BrowserRouter>
  </React.StrictMode>,
);
